import React, { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useTranslation } from "react-i18next";
import "./i18n";
import "./App.css";

import Layout from "./components/Layout";
import { AuthProvider } from "./contexts/AuthContext";
import { SiteDataProvider } from "./contexts/SiteDataContext";

import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import Rental from "./pages/Rental";
import ServiceDetail from "./pages/ServiceDetail";
import Gallery from "./pages/Gallery";
import Contact from "./pages/Contact";
import NotFound from "./pages/NotFound";

import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminInquiries from "./pages/admin/AdminInquiries";
import AdminServices from "./pages/admin/AdminServices";
import AdminGallery from "./pages/admin/AdminGallery";
import AdminClients from "./pages/admin/AdminClients";
import AdminContent from "./pages/admin/AdminContent";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

const DirectionSync = () => {
  const { i18n } = useTranslation();
  useEffect(() => {
    const lang = i18n.language?.startsWith("ar") ? "ar" : "en";
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [i18n.language]);
  return null;
};

const PublicShell = ({ children }) => <Layout>{children}</Layout>;

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <SiteDataProvider>
          <BrowserRouter>
            <ScrollToTop />
            <DirectionSync />
            <Routes>
              {/* Public site */}
              <Route path="/" element={<PublicShell><Home /></PublicShell>} />
              <Route path="/about" element={<PublicShell><About /></PublicShell>} />
              <Route path="/services" element={<PublicShell><Services /></PublicShell>} />
              <Route path="/services/rental" element={<PublicShell><Rental /></PublicShell>} />
              <Route path="/services/:slug" element={<PublicShell><ServiceDetail /></PublicShell>} />
              <Route path="/gallery" element={<PublicShell><Gallery /></PublicShell>} />
              <Route path="/contact" element={<PublicShell><Contact /></PublicShell>} />

              {/* Admin */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="inquiries" element={<AdminInquiries />} />
                <Route path="services" element={<AdminServices />} />
                <Route path="gallery" element={<AdminGallery />} />
                <Route path="clients" element={<AdminClients />} />
                <Route path="content" element={<AdminContent />} />
              </Route>

              <Route path="*" element={<PublicShell><NotFound /></PublicShell>} />
            </Routes>
            <Toaster
              position="top-right"
              richColors
              toastOptions={{
                style: {
                  borderRadius: 0,
                  border: "2px solid #111111",
                  fontFamily: "IBM Plex Sans, sans-serif",
                },
              }}
            />
          </BrowserRouter>
        </SiteDataProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
